import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { DEFAULT_PROTECTED_ROUTE_PATH } from "../../constants";
import { useGetSignedUrl } from "../../hooks/signedUrl";
import { useAuthRedirect } from "../../hooks/useAuthRedirect";
import { DocumentAccessView } from "./DocumentAccessView";
import { DocumentStatus } from "./constants";

const appendPageFragment = (url: string, pageValue: string | null): string => {
  if (!pageValue) {
    return url;
  }
  const safePage = encodeURIComponent(pageValue);
  const [base] = url.split("#");
  return `${base}#page=${safePage}`;
};

export const DocumentAccess = () => {
  const { docId } = useParams<{ docId: string }>();
  const [status, setStatus] = useState<DocumentStatus>("loading");

  const navigate = useNavigate();
  const location = useLocation();
  const { redirectToAuth } = useAuthRedirect();

  const { getSignedUrl } = useGetSignedUrl();

  useEffect(() => {
    if (!docId) {
      setStatus("error");
      return;
    }

    let isActive = true;

    const searchParams = new URLSearchParams(location.search);
    // Check both query params (?page=3) and hash fragment (#page=3)
    const pageParamFromQuery = searchParams.get("page");
    const hashMatch = location.hash.match(/[#&]page=([^&]*)/)?.[1];
    const pageParamFromHash = hashMatch ? decodeURIComponent(hashMatch) : null;
    const pageParam = pageParamFromQuery || pageParamFromHash;
    const typeParam = searchParams.get("type") || "file";

    getSignedUrl(docId, typeParam)
      .then((payload) => {
        if (!isActive) return;

        const targetUrl = appendPageFragment(payload.signed_url, pageParam);
        window.location.replace(targetUrl);
      })
      .catch((error: { response?: { status?: number } }) => {
        if (!isActive) return;

        const statusCode = error?.response?.status;

        if (statusCode === 401) {
          redirectToAuth();
        } else if (statusCode === 404) {
          setStatus("not_found");
        } else {
          setStatus("error");
        }
      });

    return () => {
      isActive = false;
    };
  }, [docId, location, getSignedUrl, redirectToAuth]);

  const handleNavigateBack = () => {
    navigate(DEFAULT_PROTECTED_ROUTE_PATH);
  };

  return <DocumentAccessView status={status} onNavigateBack={handleNavigateBack} />;
};
